import type { PayoutDetail, PayoutLine } from "./api";

export const PERIOD_TEXT = { quarterly: "Quarter", monthly: "Month", "4-4-5": "4-4-5 period", custom: "Custom days" };
export type PeriodType = keyof typeof PERIOD_TEXT;
export const PERIOD_COUNT: Record<Exclude<PeriodType, "custom">, number> = { quarterly: 4, monthly: 12, "4-4-5": 12 };

export const RUN_STATUS_TEXT = { draft: "Draft", approved: "Approved", exported: "Exported", paid: "Paid" };
export type RunStatus = keyof typeof RUN_STATUS_TEXT;
export const PAYMENT_TEXT = { due: "Due", paid: "Paid", failed: "Failed", held: "Held" };
export type Payment = keyof typeof PAYMENT_TEXT;

export const dollars = (value: number) => value.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** The run that most people make: the quarter before the quarter of today (design 9.8). */
export function lastQuarter(today: string): { year: number; number: number } {
  const [year, month] = today.split("-").map(Number);
  const quarter = Math.floor((month - 1) / 3) + 1;
  return quarter === 1 ? { year: year - 1, number: 4 } : { year, number: quarter - 1 };
}

export function quarterTag(year: number, number: number): string {
  return `${year} Q${number}`;
}

export const TAG_MAX = 60;
export const HOLD_MAX = 300;

const iso = (date: Date) => date.toISOString().slice(0, 10);
const utc = (year: number, month: number, day: number) => new Date(Date.UTC(year, month - 1, day));

/** The first and the last day of a period, as the API calculates them (F-PAY-2), or null. */
export function periodDates(periodType: PeriodType, year: number, number: number, startsOn = "", endsOn = ""): { start: string; end: string } | null {
  if (periodType === "custom") return startsOn && endsOn ? { start: startsOn, end: endsOn } : null;
  if (!Number.isInteger(year) || !Number.isInteger(number) || number < 1 || number > PERIOD_COUNT[periodType]) return null;
  if (periodType === "quarterly") return { start: iso(utc(year, 3 * number - 2, 1)), end: iso(utc(year, 3 * number + 1, 0)) };
  if (periodType === "monthly") return { start: iso(utc(year, number, 1)), end: iso(utc(year, number + 1, 0)) };
  const weeksBefore = 13 * Math.floor((number - 1) / 3) + [0, 4, 8][(number - 1) % 3];
  const start = utc(year, 1, 1 + 7 * weeksBefore);
  if (number === 12) return { start: iso(start), end: `${year}-12-31` };
  const length = number % 3 === 0 ? 5 : 4;
  return { start: iso(start), end: iso(utc(year, 1, 7 * (weeksBefore + length))) };
}

/** The name that follows the period, for example 2026 Q3, 2026-07, 2026 P7 or the days. */
export function periodTag(periodType: PeriodType, year: string, number: string, startsOn: string, endsOn: string): string {
  if (periodType === "custom") return startsOn && endsOn ? `${startsOn} to ${endsOn}` : "";
  const y = Number(year);
  const n = Number(number);
  if (!Number.isInteger(y) || !Number.isInteger(n)) return "";
  if (periodType === "quarterly") return quarterTag(y, n);
  if (periodType === "monthly") return `${y}-${String(n).padStart(2, "0")}`;
  return `${y} P${n}`;
}

/** An approved, exported or paid run that pays some of the days, or null (rule Q-25). */
export function payingRun<T extends { status: string; starts_on: string; ends_on: string }>(runs: T[], start: string, end: string): T | null {
  return runs.find((run) => run.status !== "draft" && run.starts_on <= end && run.ends_on >= start) ?? null;
}

/** "1 line", "2 lines". */
export const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export interface RunDraft {
  tag: string;
  periodType: PeriodType;
  year: string;
  number: string;
  startsOn: string;
  endsOn: string;
  marketValue: string;
  today: string;
}

/** The first problem of the run form, or null when the API can take it. */
export function runProblem(draft: RunDraft): string | null {
  if (!draft.tag.trim()) return "Give the run a name, for example 2026 Q3.";
  if (draft.tag.trim().length > TAG_MAX) return `The name can have ${TAG_MAX} characters at most.`;
  if (draft.periodType === "custom") {
    if (!draft.startsOn || !draft.endsOn) return "Give the first and the last day.";
    if (draft.endsOn < draft.startsOn) return "The last day must be on or after the first day.";
    if (draft.endsOn >= draft.today) return "A run can pay only days before today.";
  } else {
    const year = Number(draft.year);
    const number = Number(draft.number);
    if (!Number.isInteger(year) || year < 2000 || year > 2100) return "Give a year from 2000 to 2100.";
    if (!Number.isInteger(number) || number < 1 || number > PERIOD_COUNT[draft.periodType]) {
      return `Give a period from 1 to ${PERIOD_COUNT[draft.periodType]}.`;
    }
  }
  if (draft.marketValue.trim()) {
    const value = Number(draft.marketValue);
    if (!Number.isFinite(value) || value < 0 || value > 1000) return "The market value is a price from $0 to $1,000 per gallon.";
  }
  return null;
}

/** The parse of an adjustment in dollars, or a problem text. */
export function adjustmentOf(text: string, amount: number): number | string {
  const value = Number(text.trim() || "0");
  if (!Number.isFinite(value)) return "The adjustment is an amount in dollars, for example -5 or 12.50.";
  if (Math.round((amount + value) * 100) < 0) return "The adjustment cannot make the amount less than zero.";
  return Math.round(value * 100) / 100;
}

/** The totals of a run: one check for each account and payee, and the held amount. */
export function runTotals(lines: PayoutLine[]): { checks: number; paid: number; held: number; heldAmount: number } {
  const checks = new Map<string, boolean>();
  let held = 0;
  let heldAmount = 0;
  for (const line of lines) {
    if (line.payment_status === "held") {
      held += 1;
      heldAmount += line.final_amount;
    } else if (line.final_amount > 0) {
      const key = [line.number, line.payee_name.trim(), line.payee_address.trim()].join("\u0000");
      checks.set(key, (checks.get(key) ?? true) && line.payment_status === "paid");
    }
  }
  const paid = [...checks.values()].filter(Boolean).length;
  return { checks: checks.size, paid, held, heldAmount: Math.round(heldAmount * 100) / 100 };
}

/** The problem of a minimum check text, or the amount. Empty is a problem, not $0. */
export function minimumOf(text: string): number | string {
  const value = Number(text.trim());
  if (!text.trim() || !Number.isFinite(value) || value < 0 || value > 10_000) return "The minimum is an amount from $0 to $10,000.";
  return Math.round(value * 100) / 100;
}

/** Save the export as a file in the browser. */
export function download(fileName: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** A line gets the Change or Payment button: not a carried line, and not a draft that cannot be approved. */
export function lineChangeable(
  detail: Pick<PayoutDetail, "can_edit_lines" | "can_pay" | "paid_by_other">,
  line: Pick<PayoutLine, "carried_to">,
): boolean {
  if (line.carried_to) return false;
  return (detail.can_edit_lines && !detail.paid_by_other) || detail.can_pay;
}
